import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ErrorReporter from 'src/monitoring/errorReporter';
import { useAlert } from 'src/contexts/alert-context';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { NotificationsPermissionModal } from '../components/organisms/notifications-permission-modal';
import { useNotificationsEndpoints } from '../hooks/api/notifications';
import {
  dismissWebPushPromptPermanently,
  consumeWebPushPromptLoginIntent,
  isWebPushPromptPermanentlyDismissed,
  isWebPushPromptSnoozedForSession,
  snoozeWebPushPromptForSession,
} from '../hooks/push/web-push-prompt';
import {
  getWebPushDeviceId,
  hasWebPushVapidKey,
  isWebPushSupported,
  registerWebPushSubscription,
} from '../hooks/push/use-web-push';
import { NOTIFICATION_PLATFORM } from '../types/notification.types';
import type { NotificationEndpoint } from '../types/notification.types';
import { getWebPushActivationAlert } from '../utils/web-push-activation-alert.util';

interface NotificationsPushProviderProps {
  children: ReactNode;
}

export const NotificationsPushProvider: React.FC<
  NotificationsPushProviderProps
> = ({ children }) => {
  const queryClient = useQueryClient();
  const { showAlert } = useAlert();
  const { t } = useI18n();
  const canUseWebPush = hasWebPushVapidKey() && isWebPushSupported();
  const [hasLoginIntent] = useState<boolean>(() =>
    consumeWebPushPromptLoginIntent()
  );
  const [isSnoozedForSession, setIsSnoozedForSession] = useState<boolean>(() =>
    isWebPushPromptSnoozedForSession()
  );
  const [isPermanentlyDismissed, setIsPermanentlyDismissed] = useState<boolean>(
    () => isWebPushPromptPermanentlyDismissed()
  );
  const [isBootstrapping, setIsBootstrapping] = useState(canUseWebPush);
  const [isRequestingPush, setIsRequestingPush] = useState(false);
  const [hasCurrentDeviceRegistration, setHasCurrentDeviceRegistration] =
    useState(false);
  const bootstrapPromiseRef = useRef<ReturnType<
    typeof registerWebPushSubscription
  > | null>(null);

  const { data: endpoints = [], isLoading: isEndpointsLoading } =
    useNotificationsEndpoints(canUseWebPush);

  useEffect(() => {
    if (!canUseWebPush) {
      bootstrapPromiseRef.current = null;
      setIsBootstrapping(false);
      return;
    }

    let isMounted = true;
    const bootstrapPromise =
      bootstrapPromiseRef.current ??
      registerWebPushSubscription({
        requestPermission: false,
      });

    bootstrapPromiseRef.current = bootstrapPromise;

    const bootstrap = async () => {
      try {
        const result = await bootstrapPromise;

        if (!isMounted) {
          return;
        }

        if (result === 'registered') {
          setHasCurrentDeviceRegistration(true);
          void queryClient.invalidateQueries({
            queryKey: ['notifications-endpoints'],
          });
        }
      } catch (error) {
        ErrorReporter.sendException(error);
      } finally {
        bootstrapPromiseRef.current = null;
        if (isMounted) {
          setIsBootstrapping(false);
        }
      }
    };

    void bootstrap();

    return () => {
      isMounted = false;
    };
  }, [canUseWebPush, queryClient]);

  const browserPermission = canUseWebPush
    ? Notification.permission
    : 'unsupported';

  const currentDeviceEnabled = useMemo(() => {
    if (browserPermission !== 'granted') {
      return false;
    }

    if (hasCurrentDeviceRegistration) {
      return true;
    }

    const currentDeviceId = getWebPushDeviceId();

    return endpoints.some(
      (endpoint: NotificationEndpoint) =>
        endpoint.platform === NOTIFICATION_PLATFORM.WEB_PUSH &&
        endpoint.deviceId === currentDeviceId &&
        endpoint.isEnabled
    );
  }, [browserPermission, endpoints, hasCurrentDeviceRegistration]);

  const needsEndpointLookup =
    browserPermission === 'granted' && !hasCurrentDeviceRegistration;

  const canEvaluatePrompt =
    hasLoginIntent &&
    canUseWebPush &&
    !isSnoozedForSession &&
    !isPermanentlyDismissed &&
    !isBootstrapping &&
    (!needsEndpointLookup || !isEndpointsLoading);

  const shouldShowPrompt =
    canEvaluatePrompt &&
    (browserPermission !== 'granted' || !currentDeviceEnabled);

  const handleClosePrompt = () => {
    snoozeWebPushPromptForSession();
    setIsSnoozedForSession(true);
  };

  const handleDismissPrompt = () => {
    dismissWebPushPromptPermanently();
    setIsPermanentlyDismissed(true);
  };

  const handleEnablePush = async () => {
    setIsRequestingPush(true);

    try {
      const result = await registerWebPushSubscription({
        requestPermission: true,
      });

      if (result === 'registered') {
        setHasCurrentDeviceRegistration(true);
        void queryClient.invalidateQueries({
          queryKey: ['notifications-endpoints'],
        });
      } else {
        setHasCurrentDeviceRegistration(false);
      }

      const alert = getWebPushActivationAlert(result);
      showAlert(t(alert.messageKey), alert.type);
    } catch (error) {
      ErrorReporter.sendException(error);
      showAlert(t('settings.notifications.alerts.error'), 'error');
    } finally {
      setIsRequestingPush(false);
    }
  };

  return (
    <>
      {children}
      <NotificationsPermissionModal
        isOpen={shouldShowPrompt}
        loading={isRequestingPush}
        onClose={handleClosePrompt}
        onEnable={handleEnablePush}
        onDismissPermanently={handleDismissPrompt}
      />
    </>
  );
};
